import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Upload } from 'lucide-react';
import { uploadService } from '../../../services/uploadService';

interface RequiredDocument {
  id: string;
  name: string;
  category: string;
}

interface KYCDocumentChecklistProps {
  clientId: string;
  onNavigateToDocuments?: () => void; // TASK-209: Navigate to Documents tab
}

const KYCDocumentChecklist: React.FC<KYCDocumentChecklistProps> = ({ clientId, onNavigateToDocuments }) => {
  const requiredDocuments: RequiredDocument[] = [
    { id: 'id_card', name: 'ID Card / Passport', category: 'kyc' },
    { id: 'proof_of_address', name: 'Proof of Address', category: 'kyc' },
    { id: 'bank_reference', name: 'Bank Reference Letter', category: 'kyc' },
  ];

  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchKYCDocuments();
  }, [clientId]);

  const fetchKYCDocuments = async () => {
    setLoading(true);
    try {
      const docs = await uploadService.getAllUploads(clientId);
      // Filter for KYC-related documents
      const kycDocs = docs.filter((doc: any) =>
        doc.template_type === 'kyc' ||
        doc.filename.toLowerCase().includes('kyc') ||
        doc.filename.toLowerCase().includes('passport') ||
        doc.filename.toLowerCase().includes('id') ||
        doc.filename.toLowerCase().includes('address') ||
        doc.filename.toLowerCase().includes('bank')
      );
      setDocuments(kycDocs);
    } catch (error) {
      console.error('Error fetching KYC documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const isDocumentUploaded = (docType: string): boolean => {
    const searchTerms = docType.toLowerCase().split('_');
    return documents.some(doc => {
      const filename = doc.filename.toLowerCase();
      return searchTerms.some(term => filename.includes(term));
    });
  };

  if (loading) {
    return (
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="font-medium text-gray-900 mb-3">Required Documents</h4>
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 rounded w-16"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // TASK-209: Check if there are missing documents
  const missingDocuments = requiredDocuments.filter(doc => !isDocumentUploaded(doc.id));
  const hasMissingDocuments = missingDocuments.length > 0;

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <h4 className="font-medium text-gray-900 mb-3">Required Documents</h4>
      <div className="space-y-2">
        {requiredDocuments.map((doc) => {
          const uploaded = isDocumentUploaded(doc.id);
          return (
            <div key={doc.id} className="flex items-center justify-between text-sm">
              <span className="text-gray-700">{doc.name}</span>
              {uploaded ? (
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-xs">Uploaded</span>
                </span>
              ) : (
                <span className="flex items-center gap-1 text-gray-400">
                  <XCircle className="w-4 h-4" />
                  <span className="text-xs">Missing</span>
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-3">
        <p className="text-xs text-gray-500">
          {documents.length} document{documents.length !== 1 ? 's' : ''} found
        </p>

        {/* TASK-209: Show upload button when documents are missing */}
        {hasMissingDocuments && onNavigateToDocuments && (
          <button
            onClick={onNavigateToDocuments}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-kielis-navy bg-kielis-gold rounded-md hover:bg-opacity-90 transition-colors"
          >
            <Upload className="w-3 h-3" />
            Upload Documents
          </button>
        )}
      </div>
    </div>
  );
};

export default KYCDocumentChecklist;
